import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShieldCheck, XCircle, Mail, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/invitations/${token}`);
        setInvite(data);
        setName(data.name || data.email.split("@")[0]);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post(`/public/invitations/${token}/accept`, { name, password });
      await refresh();
      toast.success(`Welcome to ${invite.org_name}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Verifying invitation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8 text-center">
          <XCircle className="h-10 w-10 text-red-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          <Link to="/login">
            <Button className="mt-6 bg-slate-900 hover:bg-slate-800 text-white">
              Go to sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A1128] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative">
          <Link to="/" className="flex items-center">
            <Logo onDark className="h-8" />
          </Link>
        </div>
        <div className="relative space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs">
            <Users className="h-3 w-3 text-emerald-400" /> Team invitation
          </div>
          <h2 className="text-3xl font-bold tracking-tight">
            Join {invite.org_name} on Attestra.
          </h2>
          <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-emerald-400" />
              {invite.email}
            </div>
            <div className="text-xs text-white/60">
              Invited by{" "}
              <span className="text-white font-medium">{invite.invited_by_name}</span> ·
              role <span className="font-mono text-emerald-400 capitalize">{invite.role.replace(/_/g, " ")}</span>
            </div>
          </div>
          <div className="text-xs text-white/60">
            Expires {new Date(invite.expires_at).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold tracking-tight">
            Accept your invitation
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            You've been invited to join <span className="font-semibold">{invite.org_name}</span> on
            Attestra. Set a password to get started.
          </p>

          <form onSubmit={accept} className="mt-8 space-y-5" data-testid="invite-accept-form">
            <div>
              <Label>Email</Label>
              <Input value={invite.email} disabled className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Your name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 h-11"
                required
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label>Create a password</Label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                required
                placeholder="At least 6 characters"
                data-testid="invite-password-input"
                testIdToggle="invite-password-toggle"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              data-testid="invite-accept-btn"
            >
              <ShieldCheck className="h-4 w-4" />
              {busy ? "Joining…" : `Join ${invite.org_name}`}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-500 text-center">
            Already have an account? <Link to="/login" className="text-emerald-700 hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
